import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';
import { today, yesterday } from '../lib/dates.js';

// Manages the daily_briefing row for today. Creates it lazily on first update.
// Also exposes:
//  - resumeStep: which wizard step the user should land on
//  - topTasks: top 10 open tasks (priority high first, then scheduled_date)
//  - carriedForward: yesterday's checkout entries marked carry-forward
export function useDailyBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [topTasks, setTopTasks] = useState([]);
  const [carriedForward, setCarriedForward] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial load + realtime subscription
  useEffect(() => {
    let mounted = true;

    async function load() {
      const date = today();

      const [{ data: briefingRow }, { data: tasks }, { data: yCheckout }] =
        await Promise.all([
          supabase
            .from('daily_briefing')
            .select('*')
            .eq('date', date)
            .maybeSingle(),
          supabase
            .from('tasks')
            .select('id, title, priority, scheduled_date')
            .eq('status', 'pending')
            .order('priority', { ascending: false })
            .order('scheduled_date', { ascending: true, nullsFirst: false })
            .limit(10),
          supabase
            .from('daily_checkout')
            .select(
              'mit_1_text, mit_1_carry_forward, mit_2_text, mit_2_carry_forward, mit_3_text, mit_3_carry_forward'
            )
            .eq('date', yesterday())
            .maybeSingle(),
        ]);

      if (!mounted) return;

      setBriefing(briefingRow ?? null);
      setTopTasks(tasks ?? []);

      const carry = [];
      if (yCheckout) {
        for (const i of [1, 2, 3]) {
          if (yCheckout[`mit_${i}_carry_forward`] && yCheckout[`mit_${i}_text`]) {
            carry.push(yCheckout[`mit_${i}_text`]);
          }
        }
      }
      setCarriedForward(carry);
      setLoading(false);
    }

    load();

    // Realtime: any tab that updates today's briefing pushes to others.
    const channel = supabase
      .channel(`daily_briefing:${today()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_briefing',
          filter: `date=eq.${today()}`,
        },
        (payload) => {
          if (payload.new) setBriefing(payload.new);
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  // Upsert one or more fields into today's daily_briefing row.
  const update = useCallback(async (patch) => {
    const date = today();
    const next = { date, ...(briefing ?? {}), ...patch };
    setBriefing(next); // optimistic
    const { data, error } = await supabase
      .from('daily_briefing')
      .upsert(next, { onConflict: 'date' })
      .select()
      .single();
    if (error) {
      console.error('daily_briefing upsert failed', error);
    } else if (data) {
      setBriefing(data);
    }
    return data;
  }, [briefing]);

  // Determine which wizard step to resume on. Steps 1–7. Step 1 is always
  // displayed briefly (greeting); we resume at the first incomplete answer.
  const resumeStep = computeResumeStep(briefing);

  return {
    loading,
    briefing,
    topTasks,
    carriedForward,
    update,
    resumeStep,
    isComplete: !!(briefing && briefing.completed_at),
  };
}

function computeResumeStep(b) {
  if (!b) return 1;
  if (b.completed_at) return 7; // already done; orchestrator shows SummaryCard
  if (b.devotional === null || b.devotional === undefined) return 2;
  if (b.workout === null || b.workout === undefined) return 3;
  if (!b.bed_time) return 4;
  const mitsFilled = [1, 2, 3].every(
    (i) => b[`mit_${i}_text`] && b[`mit_${i}_text`].trim().length > 0
  );
  if (!mitsFilled) return 5;
  // Step 6 (calendar blocks) is prompt-only and not persisted; we surface it
  // before confirmation. If MITs are done, resume at 6.
  return 6;
}
